import React, { FunctionComponent, ReactNode } from 'react'
import { ExperimentProvider, Experiment, useLDClient } from '@afterpaytouch/feature'
import { useMonitoringWithEnduringEventProperties } from '../integrations/monitoring'
import { useAppDispatch, triggerExperiment, useConsumerFeatureFlagReady, useIsEmptyCheckoutToken } from '../state'
import { Features, HoldoutGroupExcludedExperiments } from '../model'

// Import existing checkout experiments
const CheckoutExperiments = {
  MerchantPreselectCheckout: 'merchant-preselect-checkout',
  EnableDobInfo: 'enable-dob-info',
  RegisterMarketingOpt: 'register-marketing-opt',
  ShowDonationMessage: 'enable-donation-experiment',
  ExchangeRateInDropdown: 'paylater.exchange-rate-in-dropdown-placement',
  CheckoutHideCardHolderName: 'checkout-hide-card-holder-name',
  CheckoutShowMerchantLogoInHeader: 'checkout-show-merchant-logo-in-header',
  CheckoutSummaryCashTooltip: 'checkout-summary-cash-tooltip-experiment',
  CheckoutShowPaymentScheduleOnAddPaymentMethod: 'checkout-show-payment-schedule-on-add-payment-method',
  PostCheckoutPersonalization: 'rocketship.show-personalization-in-post-checkout-experiment',
  CheckoutSocialProofExperiment: 'checkout-social-proof-experiment',
  CheckoutSocialProofExperimentUk: 'checkout-social-proof-experiment-uk',
  ProcessingRedirectionDelay: 'rocketship.processing-page-delay',
  CheckoutGiftCardFlowExperiment: 'checkout-gift-card-flow-experiment',
  AddFirstPaymentMethodExperiment: 'add-first-payment-method-experiment-us',
  PayIn2Experiment: 'paylater.checkout-pay-in-2-order-amount',
  SummaryPageImprovements: 'summary-page-improvements',
  NzCreditCheck: 'rocketship-nz-credit-check-enabled',
  SkipPrefillIfNoAccountExist: 'rocketship-skip-prefill-if-no-account-exist',
  PayMonthlySUP: 'paylater.pay-monthly-for-single-use-payments-enabled',
  InlineArcadeComponents: 'rocketship.inline-arcade-components',
  ConsumerLendingAutopayToggleExperiment: 'consumer-lending-autopay-toggle-experiment',
  CashCohortPreRedirectMessage: 'rocketship.cash-cohort-pre-redirect-message',
  SoftIdRetryEnabled: 'rocketship.soft-id-retry-enabled',
  ConsumerLendingEnableDynamicAprLabel: 'consumer-lending-enable-dynamic-apr-label',
} as const

export type CheckoutExperimentName = keyof typeof CheckoutExperiments

interface CheckoutExperimentProviderProps {
  children: ReactNode
}

export const CheckoutExperimentProvider: FunctionComponent<CheckoutExperimentProviderProps> = ({ children }) => {
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const dispatch = useAppDispatch()
  const client = useLDClient()
  const consumerFeatureFlagReady = useConsumerFeatureFlagReady()
  const isEmptyCheckoutToken = useIsEmptyCheckoutToken()

  const config = {
    experiments: CheckoutExperiments,
    monitoring: logEvent,

    reduxDispatch: (experiment: Experiment) => {
      dispatch(triggerExperiment([experiment.name as any, experiment.value]))
    },

    filterExperiment: (experiment: Experiment): Experiment => {
      if (HoldoutGroupExcludedExperiments.includes(experiment.name)) {
        return experiment
      }

      const isHoldoutGroup = Boolean(client?.variation(Features.CHECKOUT_HOLDOUT_GROUP_EXPERIMENT, false))

      if (isHoldoutGroup) {
        return {
          ...experiment,
          control: true,
          value: 'C',
        }
      }

      return experiment
    },

    shouldFireEvent: () => consumerFeatureFlagReady,
    validateEvent: () => !isEmptyCheckoutToken,
  }

  return <ExperimentProvider config={config}>{children}</ExperimentProvider>
}

export { CheckoutExperiments }
